/**
 * Where the Google callback sends the browser. The API could not start the
 * session itself (see the comment above /auth/google/callback), so this page
 * trades the 60-second handoff for one, on the API origin the SPA already
 * talks to, then runs the same "where now" decision as a password login.
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { readPref } from '../services/crossShellPrefs';
import { LAST_SHELL_KEY } from '../shell/houseLogin';
import { follow, settleLogin } from '../shell/postLogin';
import { withResume } from '../shell/lastSpot';
import AuthCard from '../components/AuthCard';

export default function GoogleDone() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const handoff = params.get('handoff');
    const next = params.get('next');
    (async () => {
      try {
        if (!handoff) throw new Error('no handoff');
        await api.completeGoogleSignIn(handoff);
        await refreshUser();
        const dest = await settleLogin({
          getBilling: () => api.getBilling(),
          next,
          lastShell: readPref(LAST_SHELL_KEY),
          hostname: window.location.hostname,
          origin: window.location.origin,
          fallback: withResume('/'),
        });
        follow(dest, navigate);
      } catch {
        navigate('/login?notice=google_failed', { replace: true });
      }
    })();
  }, [params, navigate, refreshUser]);

  return (
    <AuthCard title="Signing in">
      <p className="text-center text-sm text-gray-600 dark:text-gray-300">Finishing Google sign-in...</p>
    </AuthCard>
  );
}
